import { HttpError } from 'ra-core';
import { type HttpClient } from '../useHttpClient';
import { BlobReader, BlobWriter, getMimeType, ZipReader } from '@zip.js/zip.js';
import { findCommonPrefix } from '../../utils/findCommonPrefix';
import { uploadToFileHost } from './uploadFile';

export const uploadWebsite =
  (httpClient: HttpClient) =>
  async (
    zipFile: File,
    options?: {
      onUploadProgress?: (progress: {
        uploadingIndex: number;
        uploadingFileName: string;
        totalFiles: number;
      }) => void;
    }
  ) => {
    const websiteId = crypto.randomUUID();

    const allFiles = await tryGetWebsiteContents(zipFile);

    options?.onUploadProgress?.({
      uploadingIndex: 0,
      uploadingFileName: allFiles.indexHtml.relativePath,
      totalFiles: allFiles.supportingContent.length + 1,
    });
    const indexHtmlUrl = await uploadToFileHost(
      httpClient,
      await allFiles.indexHtml.toFile(),
      {
        path: `${websiteId}/${allFiles.indexHtml.relativePath}`,
        hostType: 'public',
      }
    );

    // We upload files sequentially to avoid overloading the user's network. Sequential upload also simplifies the progress reporting
    // Changing to upload multiple files in parallel would accelerate the upload, as websites often contain hundreds of files, but it should still be throttled to a reasonable number of concurrent uploads
    // Without at least some throttling, the network requests are more likely to fail due to the user's network limitations
    for (let i = 0; i < allFiles.supportingContent.length; i++) {
      const fileEntry = allFiles.supportingContent[i]!;
      options?.onUploadProgress?.({
        uploadingIndex: i + 1,
        uploadingFileName: fileEntry.relativePath,
        totalFiles: allFiles.supportingContent.length + 1,
      });

      // This doesn't retry on error, so if one file fails to upload, the whole website upload fails. This makes the upload much less stable
      await uploadToFileHost(httpClient, await fileEntry.toFile(), {
        path: `${websiteId}/${fileEntry.relativePath}`,
        hostType: 'public',
      });
    }

    return indexHtmlUrl;
  };

interface ZipFileEntry {
  relativePath: string;
  toFile: () => Promise<File>;
}

interface WebsiteZip {
  indexHtml: ZipFileEntry;
  supportingContent: ZipFileEntry[];
}

async function tryGetWebsiteContents(zipFile: File): Promise<WebsiteZip> {
  let zipReader: ZipReader<unknown> | undefined;
  try {
    zipReader = new ZipReader(new BlobReader(zipFile));

    const zipFileEntries = await zipReader.getEntries();

    let zipContentDirectory: string = findCommonPrefix(
      zipFileEntries.map((f: { filename: string }) => f.filename)
    );
    if (zipContentDirectory.includes('/')) {
      zipContentDirectory = zipContentDirectory.substring(
        0,
        zipContentDirectory.lastIndexOf('/') + 1
      );
    } else {
      zipContentDirectory = '';
    }

    let indexHtml: ZipFileEntry | undefined;
    const supportingContent: ZipFileEntry[] = [];
    for (const fileEntry of zipFileEntries) {
      if (fileEntry.directory) continue;

      const relativePath = fileEntry.filename.replace(zipContentDirectory, '');

      const zipFileEntry: ZipFileEntry = {
        relativePath,
        toFile: async () => {
          const extension = relativePath.includes('.')
            ? relativePath.substring(relativePath.lastIndexOf('.'))
            : '';
          const mimeType = extension ? getMimeType(extension) : undefined;

          // Note that this is non-streaming, which means it loads the entire file into memory. This could be an issue for very large files.
          // The maximum per-file size is ~2GB (depending on the Chrome tab's memory usage) due to this.
          const blob = await fileEntry.getData(new BlobWriter(mimeType));
          return new File([blob], relativePath, {
            type: mimeType,
          });
        },
      };

      if (relativePath === 'index.html') {
        indexHtml = zipFileEntry;
      } else {
        supportingContent.push(zipFileEntry);
      }
    }

    if (!indexHtml) {
      throw new HttpError(
        'The uploaded ZIP file must contain an index.html at its root',
        400
      );
    }

    return {
      indexHtml,
      supportingContent,
    };
  } finally {
    if (zipReader) {
      await zipReader.close();
    }
  }
}
